import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import PresenceBar from "@/components/PresenceBar";
import PrintButton from "@/components/PrintButton";
import ReorderHandle from "@/components/ReorderHandle";
import { useDemo, DEMO } from "@/lib/demo";
import { useWidgetOrder } from "@/hooks/useWidgetOrder";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const CC = "/admin/console/cc";
const TONE = { ok: "k-tag-green", warn: "k-tag-warn", bad: "k-tag-red" };

function Card({ title, sub, children, testid }) {
  return (
    <div className="k-container" data-testid={testid} style={{ padding: 20, marginBottom: 16 }}>
      {title && <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}>{title}</h3>}
      {sub && <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }}>{sub}</p>}
      {children}
    </div>
  );
}

function Home() {
  const { demo } = useDemo();
  const [live, setLive] = useState(null);
  useEffect(() => { api.get(`${CC}/home`).then(r => setLive(r.data)).catch(e => toast.error(errMsg(e))); }, []);
  const d = demo ? DEMO.ccHome : live;
  const tiles = d?.tiles || [];
  const { ordered, move } = useWidgetOrder("kadana_cc_tiles", tiles.map((t, i) => ({ key: t.label || String(i), t })));
  return (
    <>
      {ordered.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12, marginBottom: 16 }}>
          {ordered.map(({ key, t }, i) => (
            <div key={key} className="k-container" data-testid={`cc-tile-${i}`}
              style={{ padding: 16, borderLeft: `3px solid var(${t.status === "bad" ? "--accent-secondary" : t.status === "warn" ? "--warn" : "--accent-primary"})` }}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <a href={t.drill_to} className="k-link" style={{ flex: 1 }}>
                  <div className="k-muted" style={{ fontSize: 12 }}>{t.label}</div>
                  <div className="k-heading" style={{ fontSize: 24, marginTop: 4 }}>{t.value}</div>
                </a>
                <ReorderHandle testid={`cc-tile-reorder-${i}`} isFirst={i === 0} isLast={i === ordered.length - 1}
                  onUp={() => move(key, -1)} onDown={() => move(key, 1)} />
              </div>
              <span className={TONE[t.status]} style={{ fontSize: 10, marginTop: 6, display: "inline-block" }}>{t.status.toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
      <Card title="Open alerts" sub={d?.note} testid="cc-open-alerts">
        {d?.open_alerts?.length ? d.open_alerts.map(a => (
          <div key={a.id} className="k-control" style={{ marginBottom: 8 }} data-testid={`cc-alert-${a.type}`}>
            <span className={TONE[a.severity === "Critical" || a.severity === "High" ? "bad" : "warn"]} style={{ fontSize: 10 }}>{a.severity}</span>{" "}
            <span className="k-heading" style={{ fontSize: 13 }}>{a.type}</span>
            <div className="k-muted" style={{ fontSize: 12 }}>{a.message}</div>
          </div>
        )) : <p className="k-muted" style={{ fontSize: 13 }}>No open alerts.</p>}
      </Card>
    </>
  );
}

function Issues() {
  const { demo } = useDemo();
  const [live, setLive] = useState(null);
  useEffect(() => { api.get(`${CC}/open-issues`).then(r => setLive(r.data)).catch(() => {}); }, []);
  const d = demo ? DEMO.issues : live;
  return (
    <Card title="C7 · Unified open issues" sub={d?.note} testid="cc-issues">
      <table className="k-table"><thead><tr><th>Severity</th><th>Subsystem</th><th>Detail</th><th>Owner</th><th></th></tr></thead><tbody>
        {d?.issues?.map((i, k) => (
          <tr key={k} data-testid={`cc-issue-${k}`}>
            <td><span className={TONE[i.severity === "Critical" || i.severity === "High" ? "bad" : "warn"]} style={{ fontSize: 10 }}>{i.severity}</span></td>
            <td className="k-heading">{i.subsystem}</td><td className="k-muted">{i.ref}</td>
            <td className="k-muted" style={{ fontSize: 12 }}>{i.owner}</td>
            <td><a className="k-link" href={i.drill_to}>open →</a></td>
          </tr>
        ))}
      </tbody></table>
      {!d?.issues?.length && <p className="k-muted" style={{ fontSize: 13 }}>Nothing open. Quiet is good.</p>}
    </Card>
  );
}

function Fleet() {
  const [d, setD] = useState(null);
  const [br, setBr] = useState(null);
  const [q, setQ] = useState({ connector: "", region: "" });
  useEffect(() => { api.get(`${CC}/fleet-health`).then(r => setD(r.data)).catch(() => {}); }, []);
  const runBlast = () => api.get(`${CC}/blast-radius`, { params: q }).then(r => setBr(r.data)).catch(e => toast.error(errMsg(e)));
  return (
    <>
      <Card title="C3 · Generated-project fleet health" sub={d?.note} testid="cc-fleet">
        <p className="k-muted" style={{ fontSize: 13 }}>Errors by class (24h):</p>
        <table className="k-table"><tbody>{Object.entries(d?.by_error_class || {}).map(([k, v]) => <tr key={k}><td className="k-heading">{k}</td><td>{v}</td></tr>)}</tbody></table>
        <p className="k-muted" style={{ fontSize: 13, marginTop: 10 }}>Affected projects: {d?.affected_projects?.length || 0}</p>
      </Card>
      <Card title="C9 · Blast-radius estimator" sub="Real project-to-connector links, never estimated." testid="cc-blast">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="k-control" data-testid="blast-connector" placeholder="connector id (e.g. K1207)" style={{ maxWidth: 200 }} value={q.connector} onChange={e => setQ({ ...q, connector: e.target.value })} />
          <input className="k-control" data-testid="blast-region" placeholder="region code (e.g. GB)" style={{ maxWidth: 150 }} value={q.region} onChange={e => setQ({ ...q, region: e.target.value })} />
          <button className="k-btn k-btn-primary" data-testid="blast-run" onClick={runBlast}>Estimate</button>
        </div>
        {br && <p className="k-heading" style={{ fontSize: 15, marginTop: 10 }} data-testid="blast-result">{br.affected_project_count} project(s) affected · regions: {Object.keys(br.regions_affected || {}).join(", ") || "—"}</p>}
      </Card>
    </>
  );
}

function DiffTimeline() {
  const [diff, setDiff] = useState(null);
  const [tl, setTl] = useState(null);
  const [canaries, setCanaries] = useState(null);
  useEffect(() => {
    api.get(`${CC}/diff`).then(r => setDiff(r.data)).catch(() => {});
    api.get(`${CC}/correlation-timeline`).then(r => setTl(r.data)).catch(() => {});
    api.get(`${CC}/canaries`).then(r => setCanaries(r.data)).catch(() => {});
  }, []);
  const runCanaries = () => api.post(`${CC}/canaries/run`).then(r => { setCanaries({ canaries: r.data.canaries, failing: r.data.canaries.filter(c => !c.ok).map(c => c.name) }); toast.success("Canaries run"); }).catch(e => toast.error(errMsg(e)));
  return (
    <>
      <Card title="C8 · Change since yesterday" sub={diff?.note} testid="cc-diff">
        {diff?.changed?.length ? (
          <table className="k-table"><thead><tr><th>Metric</th><th>Yesterday</th><th>Today</th></tr></thead><tbody>
            {diff.changed.map((r, i) => <tr key={i}><td className="k-heading">{r.metric}</td><td className="k-muted">{String(r.yesterday)}</td><td>{String(r.today)}</td></tr>)}
          </tbody></table>
        ) : <p className="k-muted" style={{ fontSize: 13 }}>No change vs the last daily snapshot (or no snapshot yet).</p>}
      </Card>
      <Card title="C13 · Cross-subsystem correlation timeline" sub={tl?.note} testid="cc-timeline">
        <table className="k-table"><thead><tr><th>Date</th><th>Margin%</th><th>Fleet unhealthy</th><th>Watchdog</th><th>Disputes</th></tr></thead><tbody>
          {tl?.series?.map((s, i) => <tr key={i}><td className="k-heading">{s.at}</td><td>{s.margin_blended_pct ?? "—"}</td><td>{s.fleet_unhealthy}</td><td>{s.watchdog_queue}</td><td>{s.disputes}</td></tr>)}
        </tbody></table>
        {!tl?.series?.length && <p className="k-muted" style={{ fontSize: 13 }}>Timeline builds up as daily snapshots accrue.</p>}
      </Card>
      <Card title="C10 · Synthetic canaries" sub={canaries?.note} testid="cc-canaries">
        {canaries?.canaries?.map(c => (
          <div key={c.name} style={{ marginBottom: 6 }} data-testid={`canary-${c.name}`}>
            <span className={TONE[c.ok ? "ok" : "bad"]} style={{ fontSize: 10 }}>{c.ok ? "OK" : "FAIL"}</span>{" "}
            <span className="k-heading" style={{ fontSize: 13 }}>{c.name}</span> <span className="k-muted" style={{ fontSize: 12 }}>— {c.detail}</span>
          </div>
        ))}
        <button className="k-btn k-btn-ghost" data-testid="canary-run" style={{ marginTop: 8 }} onClick={runCanaries}>Run canaries</button>
      </Card>
    </>
  );
}

function Reports() {
  const [brief, setBrief] = useState(null);
  const [week, setWeek] = useState(null);
  const [runbooks, setRunbooks] = useState(null);
  useEffect(() => {
    api.get(`${CC}/daily-briefing`).then(r => setBrief(r.data)).catch(() => {});
    api.get(`${CC}/state-of-platform`, { params: { period: "week" } }).then(r => setWeek(r.data)).catch(() => {});
    api.get(`${CC}/runbooks`).then(r => setRunbooks(r.data.runbooks)).catch(() => {});
  }, []);
  return (
    <div className="k-print-doc">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><PrintButton label="Print reports" testid="cc-print-reports" /></div>
      <Card title="C6 · Daily Briefing" sub={brief?.note} testid="cc-briefing">
        <p className="k-heading" style={{ fontSize: 15 }}>{brief?.headline || "No briefing generated yet."}</p>
        <p className="k-muted" style={{ fontSize: 12 }}>{brief?.open_alerts} open · {brief?.resolved_last_24h} resolved in 24h · as of {String(brief?.at || "").slice(0, 16)}</p>
      </Card>
      <Card title="C14 · State of the Platform (weekly)" sub={week?.note} testid="cc-report">
        {week ? <p className="k-muted" style={{ fontSize: 13 }}>Issues opened {week.issues_opened} · resolved {week.issues_resolved} · new projects {week.new_projects} · blended margin {week.metrics?.margin_blended_pct ?? "—"}%</p>
          : <p className="k-muted" style={{ fontSize: 13 }}>Generates automatically on Mondays (monthly on the 1st).</p>}
      </Card>
      <Card title="C11 · Runbooks" sub="Every alert links to its ordered runbook." testid="cc-runbooks">
        {runbooks && Object.entries(runbooks).map(([k, steps]) => (
          <details key={k} style={{ marginBottom: 8 }}>
            <summary className="k-heading" style={{ fontSize: 13, cursor: "pointer" }}>{k}</summary>
            <ol className="k-muted" style={{ fontSize: 12 }}>{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </details>
        ))}
      </Card>
    </div>
  );
}

function Settings() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get(`${CC}/config`).then(r => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.put(`${CC}/config`, { thresholds: cfg.thresholds, quiet_hours: cfg.quiet_hours, channels: cfg.channels });
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!cfg) return <Card testid="cc-settings-loading"><p className="k-muted">Loading…</p></Card>;
  const num = (obj, key) => (
    <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }} className="k-muted">
      {key}
      <input className="k-control" data-testid={`cfg-${key}`} type="number" value={obj[key]}
        onChange={e => setCfg({ ...cfg, thresholds: { ...cfg.thresholds, [key]: Number(e.target.value) } })} style={{ maxWidth: 140 }} />
    </label>
  );
  return (
    <>
      <Card title="C12 · Alert thresholds (editable, not hard-coded)" testid="cc-thresholds">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {Object.keys(cfg.thresholds).map(k => <span key={k}>{num(cfg.thresholds, k)}</span>)}
        </div>
      </Card>
      <Card title="C5 · Quiet hours & channels" sub="Only Critical alerts interrupt during quiet hours; the rest roll into the Daily Briefing." testid="cc-quiet">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label className="k-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" data-testid="cfg-quiet-enabled" checked={cfg.quiet_hours.enabled} onChange={e => setCfg({ ...cfg, quiet_hours: { ...cfg.quiet_hours, enabled: e.target.checked } })} /> quiet hours on
          </label>
          <label className="k-muted" style={{ fontSize: 12 }}>start<input className="k-control" data-testid="cfg-quiet-start" type="number" value={cfg.quiet_hours.start_hour} onChange={e => setCfg({ ...cfg, quiet_hours: { ...cfg.quiet_hours, start_hour: Number(e.target.value) } })} style={{ maxWidth: 90 }} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>end<input className="k-control" data-testid="cfg-quiet-end" type="number" value={cfg.quiet_hours.end_hour} onChange={e => setCfg({ ...cfg, quiet_hours: { ...cfg.quiet_hours, end_hour: Number(e.target.value) } })} style={{ maxWidth: 90 }} /></label>
          <label className="k-muted" style={{ fontSize: 12, flex: 1, minWidth: 200 }}>alert email<input className="k-control" data-testid="cfg-email" value={cfg.channels.email_to || ""} onChange={e => setCfg({ ...cfg, channels: { ...cfg.channels, email_to: e.target.value } })} /></label>
          <label className="k-muted" style={{ fontSize: 12, flex: 1, minWidth: 200 }}>webhook (Slack) URL<input className="k-control" data-testid="cfg-webhook" value={cfg.channels.webhook_url || ""} onChange={e => setCfg({ ...cfg, channels: { ...cfg.channels, webhook_url: e.target.value } })} /></label>
        </div>
        <button className="k-btn k-btn-primary" data-testid="cfg-save" style={{ marginTop: 12 }} onClick={save}>Save config</button>
      </Card>
    </>
  );
}

const TABS = [
  ["home", "Command Center (C1)", Home],
  ["issues", "Open Issues (C7)", Issues],
  ["fleet", "Fleet & Blast-radius (C3,C9)", Fleet],
  ["diff", "Diff · Timeline · Canaries (C8,C13,C10)", DiffTimeline],
  ["reports", "Briefing · Report · Runbooks (C6,C14,C11)", Reports],
  ["settings", "Thresholds & Quiet Hours (C12,C5)", Settings],
];

export default function CommandCenter() {
  const [tab, setTab] = useState("home");
  const Active = TABS.find(t => t[0] === tab)[2];
  return (
    <Shell active="admin">
      <h1 className="k-heading" style={{ fontSize: 28, marginTop: 0 }}>Command Center</h1>
      <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>Patch 31 · the one pane of glass above the menus — surfaces existing data, adds only lightweight non-LLM checks. External alerts are severity-tiered with quiet hours so the system stays sustainable.</p>
      <PresenceBar scope="command-center" testid="cc-presence" />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 20px" }}>
        {TABS.map(([k, label]) => (
          <button key={k} className={tab === k ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"} data-testid={`cc-tab-${k}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      <Active />
    </Shell>
  );
}
